import {
  CreditPlanResponseModel,
  CreditPlanResultItem,
  ItemListAnalyticsModel
} from '@/@types/client-api';
import { AnalyticsContext } from '@/analytics/constants';
import Text from '@/components/cms/Text';
import InlineDebugInformation from '@/components/ui/CreditPlan/InlineDebugInformation';
import Divider from '@/components/ui/Divider';
import useSearchModalPanelState from '@/components/ui/SearchResults/hooks/useSearchModalPanelState';
import SearchResultsRow from '@/components/ui/SearchResults/SearchResultsRow';
import { useAppSelector } from '@/hooks';
import { selectCreditPlansDebugModeEnabled } from '@/redux/selectors/creditPlanSelectors';
import { FC, Fragment, ReactNode, useCallback, useLayoutEffect, useMemo } from 'react';
import withPliErrorBoundary from '../ErrorBoundary/withPliErrorBoundary';
import { useErrorBoundary } from 'react-error-boundary';
import { ComponentFailurePlaceholderBlockProps } from '../ErrorFallback/ComponentFailurePlaceholderBlock';
import {
  getCreditCalloutItems,
  getCreditPlanItemSavedState
} from '@/components/ui/CreditPlan/util/CreditPlanHelpers';
import CreditCallout from '@/components/ui/CreditCallout';

interface CreditPlanSearchResultBlockProps extends Pick<
  ComponentFailurePlaceholderBlockProps,
  'onRetryButtonClick'
> {
  /** Credit plan response containing the result groups and metadata. */
  creditPlanResponseModel: CreditPlanResponseModel;
  /** Whether the credit plan results are loading. */
  isLoading?: boolean;
  /** Saving status keyed by item variation PK. */
  itemSavingStatus?: Record<string, boolean>;
  /** Saved-state overrides keyed by item variation PK. */
  savedItemOverrides?: Record<string, boolean>;
  /** Analytics metadata for the rendered item list. */
  itemListAnalyticsModel: ItemListAnalyticsModel;
  /** Analytics context for result interactions. */
  analyticsContext: AnalyticsContext;
  /** Removes a single credit plan item from the library. */
  handleSingleRemoveFromLibrary?: (item: CreditPlanResultItem) => void;
  /** Saves a single credit plan item to the library. */
  handleSingleSaveToLibrary?: (item: CreditPlanResultItem) => void;
  /** Replaces the default snippet actions for an individual result item. */
  overrideActions?: (item: CreditPlanResultItem) => ReactNode;
  /** Retries loading the credit plan results. */
  onRetryButtonClick?: () => void;
}

const CreditPlanSearchResultBlock: FC<CreditPlanSearchResultBlockProps> = ({
  creditPlanResponseModel,
  isLoading,
  itemSavingStatus = {},
  savedItemOverrides = {},
  itemListAnalyticsModel,
  analyticsContext,
  handleSingleRemoveFromLibrary,
  handleSingleSaveToLibrary,
  overrideActions
}) => {
  const { showBoundary } = useErrorBoundary();

  useLayoutEffect(() => {
    if ((creditPlanResponseModel?.creditPlan?.creditProductGroups?.length || 0) == 0)
      showBoundary(new Error('No credit plan results available.'));
  }, [creditPlanResponseModel?.creditPlan?.creditProductGroups?.length, showBoundary]);

  const searchState = useSearchModalPanelState();
  const debugModeEnabled = useAppSelector(selectCreditPlansDebugModeEnabled);
  const debugInformation = useMemo(
    () => creditPlanResponseModel?.debugInformation,
    [creditPlanResponseModel?.debugInformation]
  );

  const plan = useMemo(
    () => creditPlanResponseModel?.creditPlan,
    [creditPlanResponseModel?.creditPlan]
  );
  const jurisdictionId = useMemo(
    () => creditPlanResponseModel?.jurisdiction,
    [creditPlanResponseModel?.jurisdiction]
  );
  const calloutItemsByGroup = useMemo(
    () =>
      plan?.creditProductGroups?.map(group =>
        getCreditCalloutItems(group.searchResultItems ?? [], group.groupKey, plan, jurisdictionId)
      ),
    [jurisdictionId, plan]
  );

  const getExtraItemContent = useCallback(
    (item: CreditPlanResultItem, groupIndex: number, itemIndex: number) => {
      const calloutItems = calloutItemsByGroup?.[groupIndex][itemIndex] ?? [];
      const items: ReactNode[] = [];

      if (calloutItems.length) {
        items.push(
          <div className="w-full">
            <CreditCallout
              items={calloutItems}
              classNames="text-2-fixed-medium mr-2 rounded-md bg-gray-lightest px-2 py-1 text-left leading-4"
            />
          </div>
        );
      }

      if ((item.partialCreditText?.length || 0) > 0) {
        items.push(<Text className="text-2 mt-1 text-gray-dark">{item.partialCreditText!}</Text>);
      }

      return items.length > 0 ? (
        <div className="flex w-full flex-col items-start justify-start">
          {items.map((a, i) => (
            <Fragment key={i}>{a}</Fragment>
          ))}
        </div>
      ) : undefined;
    },
    [calloutItemsByGroup]
  );

  return plan?.creditProductGroups?.map((group, groupIndex) => (
    <div key={groupIndex} className="py-12 lg:py-10">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <Text className="heading-5-medium">{group.heading}</Text>
        </div>
        {!!group?.totalCreditText && (
          <div className="flex items-center justify-end whitespace-nowrap">
            {/* <Icon name='credits' size='medium' className='mr-1'/> */}
            <Text className="text-1-medium">{group.totalCreditText}</Text>
          </div>
        )}
      </div>

      <Divider color="light" className="my-10" />

      <div className="flex flex-col">
        {group.searchResultItems?.map((item, i) => {
          const isSavedInLibrary = getCreditPlanItemSavedState(item, savedItemOverrides);
          const isItemSaving = !!item.variationPk && !!itemSavingStatus?.[item.variationPk];
          const extraContent = getExtraItemContent(item, groupIndex, i);

          return (
            <div key={item.variationPk} className="my-4">
              <SearchResultsRow
                resultItem={item}
                onSecondaryButtonClick={() => {
                  if (isItemSaving) return;
                  if (isSavedInLibrary) handleSingleRemoveFromLibrary?.(item);
                  else handleSingleSaveToLibrary?.(item);
                }}
                secondaryButtonIsLoading={isItemSaving}
                secondaryButtonIsSaved={isSavedInLibrary}
                index={i}
                isLoading={!!isLoading}
                listName={itemListAnalyticsModel.listName || ''}
                listId={itemListAnalyticsModel.listId || ''}
                modalPanelState={searchState}
                hideSeeMore={true}
                analyticsContext={analyticsContext}
                itemListAnalytics={itemListAnalyticsModel}
                additionalContent={extraContent}
                overrideActions={overrideActions?.(item)}
              />
              {!!debugModeEnabled && !!debugInformation?.allowed && (
                <InlineDebugInformation
                  credits={item.credits ?? []}
                  jurisdictionId={jurisdictionId ?? ''}
                  className="mt-6"
                  pk={item.variationPk ?? ''}
                  isPersonalizedItem={item.isPersonalizedItem}
                />
              )}

              {i < group.searchResultItems!.length - 1 && (
                <Divider color="light" className="my-10" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  ));
};

const CreditPlanSearchResultBlockWithErrorBoundary = withPliErrorBoundary(
  CreditPlanSearchResultBlock,
  {
    name: 'CreditPlanSearchResultBlock',
    sectionClassNames: '!px-0',
    buttonClassNames: '!w-fit'
  }
);

export default CreditPlanSearchResultBlockWithErrorBoundary;
